import { defineStore } from 'pinia';

import { getHttpErrorData, getHttpErrorStatus } from '@/apis/http-errors';
import { fetchUsers, getTwitterAccount, getTwitterAccounts } from '@/apis/twitter';
import { logger } from '@/utils/logger';

interface TwitterUser {
  username: string;
  [key: string]: unknown;
}

interface TwitterAccount {
  brand_id: number;
  [key: string]: unknown;
}

export const useTwitterStore = defineStore('twitter', {
  // Accounts cover every identity brand; App.vue loads them per user.
  resetOnBrandChange: { keep: ['twitterAccounts'] },
  state: () => ({
    // consider if state value should be cleared in resetState() action
    users: {} as Record<string, Readonly<TwitterUser>>,
    requests: {
      users: {} as Partial<Record<string, Promise<unknown>>>,
      searches: {} as Partial<Record<string, Promise<Readonly<TwitterUser>[]>>>,
    },
    twitterAccount: null as TwitterAccount | null,
    twitterAccounts: null as TwitterAccount[] | null,
  }),
  getters: {
    getUser: (state) => (username: string) => state.users[username.toLowerCase()],
    twitterAccountsMap: (state) => {
      const result = Object.fromEntries(
        (state.twitterAccounts || []).map((account) => [account.brand_id, { ...account }]),
      );
      return result;
    },
  },
  actions: {
    resetState() {
      /*
     Reset state for events such as changing brands.
     Some state values such as twitterAccounts are reloaded but are not wanted to be nulled
     while fetching most recent account list.
      */
      this.users = {};
      this.requests = {
        users: {},
        searches: {},
      };
      this.twitterAccount = null;
    },
    addUsers(users: TwitterUser[]) {
      users.forEach((user) => {
        this.users = {
          ...this.users,
          [user.username.toLowerCase()]: Object.freeze(user),
        };
      });
    },
    async searchUsers(query: string, brandId?: number) {
      if (this.requests.searches[query] === undefined) {
        const request = (async () => {
          const res = await fetchUsers({ handles: query, brandId });
          const users: TwitterUser[] = res.data;
          this.addUsers(users);
          return users.map((user) => this.users[user.username.toLowerCase()]);
        })();
        this.requests.searches = {
          ...this.requests.searches,
          [query]: request,
        };
        request.catch(() => {
          const newValue = { ...this.requests.searches };
          delete newValue[query];
          this.requests.searches = newValue;
        });
      }
      return this.requests.searches[query];
    },
    async fetchUsers(usernames: string[], brandId?: number) {
      const lowercaseUsernames = usernames.map((username) => username.toLowerCase());
      const missingUsernames = lowercaseUsernames.filter(
        (username) => this.requests.users[username] === undefined && !this.users[username],
      );
      if (missingUsernames.length > 0) {
        const request = fetchUsers({ handles: missingUsernames.join(','), brandId })
          .then((response) => this.addUsers(response.data))
          .catch(() =>
            lowercaseUsernames.forEach((username) => {
              const newValue = { ...this.requests.users };
              delete newValue[username];
              this.requests.users = newValue;
            }),
          );
        lowercaseUsernames.forEach((username) => {
          this.requests.users = {
            ...this.requests.users,
            [username]: request,
          };
        });
      }
      await Promise.all(
        lowercaseUsernames.map((username) => this.requests.users[username] ?? Promise.resolve()),
      );
    },
    async getTwitterAccount({ brandId }: { brandId: number }) {
      try {
        const response = await getTwitterAccount({ brandId });
        this.twitterAccount = response?.data;
      } catch (error) {
        if (getHttpErrorStatus(error) === 404) {
          this.twitterAccount = null;
        } else {
          throw error;
        }
      }
      return this.twitterAccount;
    },
    async getTwitterAccounts({ brandIds }: { brandIds: number[] }) {
      try {
        const response = await getTwitterAccounts({ brandIds });
        this.twitterAccounts = response?.data;
      } catch (error) {
        const status = getHttpErrorStatus(error);
        if (status === 404) {
          this.twitterAccounts = null;
        } else if (status === 401) {
          this.twitterAccounts = null;
          logger.error(
            `twitterStore getTwitterAccounts request returned authentication error: ${(getHttpErrorData(error) as { description?: string } | undefined)?.description}`,
            {},
            error,
          );
        } else {
          throw error;
        }
      }
      return this.twitterAccounts;
    },
  },
});
